import { apiEmployeeTimeOff } from "@/api/employees/employee-time-off.api.ts";
import { apiEmployees } from "@/api/employees/employee.api";
import { IMPERSONATION_TOKEN_KEY } from "@/api/axios.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import i18n from "@/i18n.ts";
import { hiringPlanKeys } from "@/queries/recruit/use-hiring-plan.query.ts";
import { appKeys } from "@/queries/settings/use-app.query.ts";
import analyticsService from "@/services/analytics-service.ts";
import { useAuthStore } from "@/stores/employee.store.ts";
import { useThemeStore } from "@/stores/theme.store.ts";
import { ApiErrorException, ApiErrors, ApiPassException } from "@/types/api.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiEmployeeHireJobQueued,
  ProcessingJobStatus,
} from "@/types/employees/employee-hire.types";
import { isNotFoundApiError } from "@/types/api.types";
import {
  ApiEmployeeAccountSettingsSavePayload,
  ApiEmployeeBanPayload,
  ApiEmployeeEditPayload,
  ApiEmployeeFilter,
  ApiEmployeeTimeOffCategoryPayload,
  ApiTwoFactorSetupVerifyPayload,
  ApiMeEmployee,
  ChangePasswordSource,
  EmployeeFilterDefault,
  getEmployeeAvatarUrl,
  PortalAccessMode,
} from "@/types/employees/employee.types.ts";
import { ApiHiringPlanFilter } from "@/types/recruit/hiring-plan.types.ts";
import { ApiInviteEmailType } from "@/types/workflows/workflow.action-notification.types.ts";
import {
  ApiEmployeeTimeOffHistoryFilter,
  ApiEmployeeTimeOffRequestsFilter,
} from "@/types/time-off/time-off.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import * as Sentry from "@sentry/react";
import { SelectItem } from "@skailer-com/uikit";
import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";

export const employeesKeys = createQueryKeys("employees", {
  inviteLoginMethods: (token: string | undefined) => ({
    queryKey: [token],
    queryFn: async ({ signal }) => {
      if (!token) throw new ApiPassException("Invite token is required");
      const result = await apiEmployees.getInviteLoginMethods(token, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  hireJobStatus: (jobId: number | undefined, accessKey: string | undefined) => ({
    queryKey: [jobId, accessKey],
    queryFn: async () => {
      if (jobId === undefined || accessKey === undefined)
        throw new ApiPassException("Job is required");
      const result = await apiEmployees.hireJobStatus(jobId, accessKey);
      if (!result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  search: (filter: ApiEmployeeFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiEmployees.search(filter, signal);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
  searchInfinite: (filter: ApiEmployeeFilter) => ({
    queryKey: [filter],
  }),
  searchColumns: (organizationId: number | undefined) => ({
    queryKey: [organizationId],
    queryFn: async ({ signal }) => {
      const result = await apiEmployees.searchColumns(signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  searchWithRoles: (filter: ApiEmployeeFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiEmployees.searchWithRoles(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  detail: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Employee id is required");
      const result = await apiEmployees.getById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  me: null,
  byListOfId: (ids: number[]) => ({
    queryKey: [ids],
    queryFn: async () => {
      const result = await apiEmployees.getByListOfId(ids);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  timeOffCategories: (employeeId: number, payload: ApiEmployeeTimeOffCategoryPayload) => ({
    queryKey: [
      employeeId,
      { ...payload, ...{ date_balance: format(payload.date_balance, "yyyy-MM-dd") } },
    ],
    queryFn: async () => {
      const result = await apiEmployeeTimeOff.categories(employeeId, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  timeOffRequests: (employeeId: number, filter: ApiEmployeeTimeOffRequestsFilter) => ({
    queryKey: [employeeId, filter],
    queryFn: async () => {
      const result = await apiEmployeeTimeOff.requests(employeeId, filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  timeOffHistory: (employeeId: number, filter: ApiEmployeeTimeOffHistoryFilter) => ({
    queryKey: [employeeId, filter],
    queryFn: async () => {
      const result = await apiEmployeeTimeOff.history(employeeId, filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  timeOffCalendar: (employeeId: number, year: number) => ({
    queryKey: [employeeId, year],
    queryFn: async () => {
      const result = await apiEmployeeTimeOff.calendar(employeeId, year);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  hiringPlanSearch: (employeeId: number, filter?: ApiHiringPlanFilter) => ({
    queryKey: [employeeId, filter],
  }),
  prefillCandidate: (candidateId: number | undefined) => ({
    queryKey: [candidateId],
    queryFn: async () => {
      if (candidateId == undefined) throw new ApiPassException("Employee id is required");
      const result = await apiEmployees.prefillCandidate(candidateId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useEmployeeMe = (enabled: boolean = true) => {
  const { token, setUserId, setToken } = useAuthStore();
  const { setTheme } = useThemeStore();

  return useQuery({
    queryKey: employeesKeys.me.queryKey,
    queryFn: async () => {
      // Флаг читаем до await: на 401 интерцептор синхронно сбросит impersonation-токен, и setToken(null) разлогинит админа.
      const isImpersonating = !!sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);

      const result = await apiEmployees.getMe();

      if (result.error) {
        if (!isImpersonating && result.error.status === ApiErrors.accessDenied) {
          setToken(null);
        }
        throw new ApiErrorException(result.error);
      }

      if (result.data) {
        const employee = result.data;

        Sentry.setUser({
          id: employee.id,
          email: employee.email || employee.email_personal || undefined,
        });

        analyticsService.setUser(employee);

        // Персист-сторы шарятся между вкладками через localStorage — под impersonation их запись затрёт сессию админа.
        if (!isImpersonating) {
          setUserId(employee.id);
          i18n.changeLanguage(employee.language);

          if (employee.theme) {
            setTheme(employee.theme);
          }
        }
      }

      return result.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) =>
      query.state.data && query.state.data.portal_access_mode !== PortalAccessMode.full
        ? 15000
        : false,
    enabled: enabled && !!token,
  });
};

export const useEmployees = (filter: ApiEmployeeFilter, enabled: boolean = true) => {
  return useQuery({
    ...employeesKeys.search(filter),
    enabled: enabled,
  });
};

export const useEmployeeSearchColumns = (enabled: boolean = true) => {
  const { data: employeeMe } = useEmployeeMe(enabled);
  const organizationId = employeeMe?.organization_id;

  return useQuery({
    ...employeesKeys.searchColumns(organizationId),
    enabled: enabled && organizationId !== undefined,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

export const useEmployeesInfinite = (filter: ApiEmployeeFilter) => {
  return useInfiniteQuery({
    queryKey: employeesKeys.searchInfinite(filter).queryKey,
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiEmployees.search({ ...filter, page_number: pageParam }, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPageParam) =>
      lastPageParam && lastPageParam.metadata.page_number < lastPageParam.metadata.total_pages
        ? lastPageParam.metadata.page_number + 1
        : undefined,
  });
};

export const useEmployeesSearchWithRoles = (filter: ApiEmployeeFilter) => {
  return useQuery({
    ...employeesKeys.searchWithRoles(filter),
  });
};

export const useEmployeeDetails = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...employeesKeys.detail(id),
    enabled: enabled && !!id,
  });
};

export interface SaveParams {
  id: number;
  payload: ApiEmployeeEditPayload;
}

export const useEmployeeUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined,
  fieldMap: Record<string, string> = {}
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: SaveParams) {
      const result = await apiEmployees.update(id, payload);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: (employee) => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(employee.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control, fieldMap);
    },
  });
};

export interface EmployeeSetAvatarParams {
  fileUUID: string;
  id: number;
}

const invalidateEmployeeQueries = (queryClient: QueryClient, employeeId: number) => {
  queryClient.invalidateQueries({
    queryKey: employeesKeys.detail(employeeId).queryKey,
  });
  queryClient.invalidateQueries({
    queryKey: employeesKeys.me.queryKey,
  });
  queryClient.invalidateQueries({
    queryKey: employeesKeys.search._def,
  });
};

export const useEmployeeSetAvatar = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, fileUUID }: EmployeeSetAvatarParams) => {
      const result = await apiEmployees.setAvatar(id, fileUUID);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      invalidateEmployeeQueries(queryClient, variables.id);
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeDeleteAvatar = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (employeeId: number) => {
      const result = await apiEmployees.deleteAvatar(employeeId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, employeeId) => {
      invalidateEmployeeQueries(queryClient, employeeId);
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeResetConfirmEmail = () => {
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async () => {
      const result = await apiEmployees.resetConfirmEmail();

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeSendInviteConfirmEmail = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      employeeId,
      inviteEmailType,
    }: {
      employeeId: number;
      inviteEmailType: ApiInviteEmailType;
    }) => {
      const { success, error } = await apiEmployees.resentInviteEmail(employeeId, inviteEmailType);

      if (error) throw new ApiErrorException(error);

      return success;
    },
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeCreateInviteLink = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      employeeId,
      inviteEmailType,
    }: {
      employeeId: number;
      inviteEmailType: ApiInviteEmailType;
    }) => {
      const result = await apiEmployees.createInviteLink(employeeId, inviteEmailType);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeUpdateAccountSettings = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: ApiEmployeeAccountSettingsSavePayload) => {
      const employee = queryClient.getQueryData<ApiMeEmployee>(employeesKeys.me.queryKey);
      const result = await apiEmployees.updateAccountSettings({
        language: employee?.language,
        timezone: employee?.timezone,
        ...payload,
      });

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: (employee, payload) => {
      if (employee) {
        queryClient.setQueryData(employeesKeys.me.queryKey, employee);
      }

      queryClient.invalidateQueries({
        queryKey: employeesKeys.me.queryKey,
      });

      if (typeof payload.show_full_salary === "boolean") {
        queryClient.invalidateQueries({
          queryKey: appKeys.config.queryKey,
        });
      }
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export default function useEmployeeSearchInline(
  enabled: boolean = true,
  defaultFilter?: ApiEmployeeFilter
) {
  const { renderEmployeeName } = useFormatter();

  const [filter, setFilter] = useState<ApiEmployeeFilter>(defaultFilter ?? EmployeeFilterDefault);

  const { data, isLoading } = useEmployees(filter, enabled);
  const options = useMemo(() => {
    if (data?.items) {
      return data.items.map((x) => {
        // const positionTitle =
        return {
          id: x.id,
          title: renderEmployeeName(x),
          avatarUrl: getEmployeeAvatarUrl(x, "sm"),
          caption: getEmployeePositionTitle(x),
        };
      }) as SelectItem[];
    }

    return [] as SelectItem[];
  }, [data]);

  function search(text: string | undefined) {
    setFilter({ ...filter, text: text });
  }

  return {
    data,
    isLoading,
    options,
    search,
    filter,
    setFilter,
  };
}

export const useEmployeeMeInvalidate = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: employeesKeys.me.queryKey,
    });
  };
};

export const useImpersonationStart = () => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ employeeId, reason }: { employeeId: number; reason?: string }) => {
      const result = await apiEmployees.impersonationStart(employeeId, reason);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useImpersonationEnd = () => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async () => {
      const result = await apiEmployees.impersonationEnd();

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useInviteLoginMethods = (token: string | undefined, enabled: boolean) =>
  useQuery({
    ...employeesKeys.inviteLoginMethods(token),
    enabled: enabled && !!token,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: false,
  });

export const useInviteCodeRequest = () =>
  useMutation({
    mutationFn: async (token: string) => {
      const result = await apiEmployees.requestInviteCode(token);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
  });

export const useInviteCodeVerify = () =>
  useMutation({
    mutationFn: async ({ token, code }: { token: string; code: string }) => {
      const result = await apiEmployees.verifyInviteCode(token, code);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
  });

export const useEmployeesByIds = (ids: number[], enabled: boolean = true) => {
  return useQuery({
    ...employeesKeys.byListOfId(ids),
    enabled: enabled && ids.length > 0,
  });
};

export interface EmployeeSetBackgroundParams {
  fileUUID: string;
  id: number;
}

export const useEmployeeSetBackground = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, fileUUID }: EmployeeSetBackgroundParams) => {
      const result = await apiEmployees.setBackground(id, fileUUID);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      invalidateEmployeeQueries(queryClient, variables.id);
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeDeleteBackground = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (employeeId: number) => {
      const result = await apiEmployees.deleteBackground(employeeId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, employeeId) => {
      invalidateEmployeeQueries(queryClient, employeeId);
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeTimeOffCategoriesData = (
  employeeId: number,
  payload: ApiEmployeeTimeOffCategoryPayload,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeesKeys.timeOffCategories(employeeId, payload),
    enabled: enabled,
    staleTime: 60 * 1000,
  });
};

export const useEmployeeTimeOffRequests = (
  employeeId: number,
  filter: ApiEmployeeTimeOffRequestsFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeesKeys.timeOffRequests(employeeId, filter),
    enabled: enabled,
  });
};

export const useEmployeeTimeOffHistory = (
  employeeId: number,
  filter: ApiEmployeeTimeOffHistoryFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeesKeys.timeOffHistory(employeeId, filter),
    enabled: enabled,
  });
};

export const useEmployeeTimeOffCalendar = (
  employeeId: number,
  year: number,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeesKeys.timeOffCalendar(employeeId, year),
    enabled: enabled,
  });
};

export const useEmployeeHiringPlanSearch = (
  employeeId: number,
  filter?: ApiHiringPlanFilter,
  enabled: boolean = true
) => {
  const queryClient = useQueryClient();

  return useQuery({
    ...employeesKeys.hiringPlanSearch(employeeId, filter),
    queryFn: async () => {
      if (!filter) return;
      const result = await apiEmployees.hiringPlanSearch(employeeId, filter);
      if (result.error) throw new ApiErrorException(result.error);

      result.data?.items.forEach((item) => {
        queryClient.setQueryData(
          hiringPlanKeys.requestDetails(item.hiring_plan_data.id).queryKey,
          item
        );
      });

      return result.data;
    },
    enabled: enabled,
    staleTime: 60 * 1000,
  });
};

export const useEmployeePrefill = (candidateId: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...employeesKeys.prefillCandidate(candidateId),
    enabled: enabled && !!candidateId,
  });
};

export const useEmployeeTwoFactorSetup = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async () => {
      const result = await apiEmployees.twoFactorSetup();

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.me.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeTwoFactorSetupVerify = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: ApiTwoFactorSetupVerifyPayload) => {
      const result = await apiEmployees.twoFactorSetupVerify(payload);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.me.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeTwoFactorDisable = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: ApiTwoFactorSetupVerifyPayload) => {
      const result = await apiEmployees.twoFactorDisable(payload);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.me.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeTwoFactorDisableForce = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (employeeId: number) => {
      const result = await apiEmployees.twoFactorDisableForce(employeeId);

      if (result.error) throw new ApiErrorException(result.error);

      return { employeeId, result: result.data };
    },
    onSuccess: ({ employeeId }) => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeTwoFactorVerify = () => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ access_token, code }: { access_token: string; code: string }) => {
      const result = await apiEmployees.twoFactorVerify(access_token, code);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeChangePassword = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      password,
      source,
      accessToken,
    }: {
      password: string;
      source: ChangePasswordSource;
      accessToken?: string;
    }) => {
      const result = await apiEmployees.changePassword(password, source, accessToken);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmployeeResetPassword = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      const result = await apiEmployees.resetPassword(token);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
  });
};

export const useEmployeeDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      employeeId,
      successorEmployeeId,
    }: {
      employeeId: number;
      successorEmployeeId: number;
    }) => {
      const result = await apiEmployees.delete(employeeId, successorEmployeeId);

      if (result.error) throw new ApiErrorException(result.error);

      return { employeeId, result: result.data };
    },
    onSuccess: ({ employeeId }) => {
      queryClient.removeQueries({
        queryKey: employeesKeys.detail(employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

interface EmployeeBanUpdateParams {
  employeeId: number;
  payload: ApiEmployeeBanPayload;
}

export const useEmployeeUpdateBan = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ employeeId, payload }: EmployeeBanUpdateParams) => {
      const result = await apiEmployees.updateBan(employeeId, payload);

      if (result.error) throw new ApiErrorException(result.error);

      return { employeeId, result: result.data };
    },
    onSuccess: ({ employeeId }) => {
      invalidateEmployeeQueries(queryClient, employeeId);
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeHire = () => useMutation({ mutationFn: apiEmployees.hire, retry: false });

export const useEmployeeHireJobStatus = (job: ApiEmployeeHireJobQueued | null) =>
  useQuery({
    ...employeesKeys.hireJobStatus(job?.job_id, job?.access_key),
    enabled: job !== null,
    gcTime: 0,
    retry: (failureCount, error) => !isNotFoundApiError(error) && failureCount < 2,
    retryDelay: 1500,
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      const status = query.state.data?.status;
      return status === ProcessingJobStatus.completed || status === ProcessingJobStatus.failed
        ? false
        : 1500;
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
